import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PwaInstallService } from '../../services/pwa-install.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-pwa-install-prompt',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Prompt de instalación -->
    <div class="install-prompt-overlay" *ngIf="showInstallPrompt" (click)="dismissPrompt()">
      <div class="install-prompt-content" (click)="$event.stopPropagation()">
        <div class="prompt-header">
          <div class="app-icon">📱</div>
          <h3 class="prompt-title">¡Instala BarClik!</h3>
          <button class="close-btn" (click)="dismissPrompt()">✕</button>
        </div>
        
        <div class="prompt-body">
          <p class="prompt-description">
            Instala BarClik en tu dispositivo para una mejor experiencia:
          </p>
          <ul class="benefits-list">
            <li>🚀 Acceso rápido desde tu pantalla de inicio</li>
            <li>📱 Funciona como una app nativa</li>
            <li>🔔 Recibe notificaciones push</li>
            <li>⚡ Funciona sin conexión</li>
          </ul>
        </div>
        
        <div class="prompt-actions">
          <button class="btn-dismiss" (click)="dismissPrompt()">
            Ahora no
          </button>
          <button class="btn-install" (click)="installApp()">
            📱 Instalar
          </button>
        </div>
      </div>
    </div>

    <!-- Notificación de actualización -->
    <div class="update-notification" *ngIf="showUpdateNotification">
      <div class="notification-content">
        <div class="notification-icon">🔄</div>
        <div class="notification-text">
          <h4>¡Nueva actualización disponible!</h4>
          <p>Hay mejoras y nuevas funciones esperándote</p>
        </div>
        <div class="notification-actions">
          <button class="btn-update" (click)="updateApp()">
            Actualizar
          </button>
          <button class="btn-close" (click)="dismissUpdate()">✕</button>
        </div>
      </div>
    </div>

    <!-- Banner de instalación compacto -->
    <div class="install-banner" *ngIf="showCompactBanner">
      <div class="banner-content">
        <div class="banner-icon">📱</div>
        <div class="banner-text">
          <span>Instala BarClik para mejor experiencia</span>
        </div>
        <div class="banner-actions">
          <button class="btn-banner-install" (click)="installApp()">
            Instalar
          </button>
          <button class="btn-banner-close" (click)="dismissBanner()">✕</button>
        </div>
      </div>
    </div>
  `,
  styleUrl: './pwa-install-prompt.component.scss'
})
export class PwaInstallPromptComponent implements OnInit, OnDestroy {
  showInstallPrompt = false;
  showUpdateNotification = false;
  showCompactBanner = false;
  
  private destroy$ = new Subject<void>();
  private promptShownTime = 0;

  constructor(private pwaInstallService: PwaInstallService) {}

  ngOnInit(): void {
    // Suscribirse a los estados de instalación y actualización
    this.pwaInstallService.installAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(available => {
        if (available) {
          this.scheduleInstallPrompt();
        }
      });

    this.pwaInstallService.updateAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(available => {
        this.showUpdateNotification = available;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private scheduleInstallPrompt(): void {
    // Mostrar el banner compacto inmediatamente
    this.showCompactBanner = true;
    
    // Mostrar el prompt completo después de 30 segundos de uso
    setTimeout(() => {
      if (this.pwaInstallService.isInstallAvailable()) {
        this.showFullPrompt();
      }
    }, 30000);
  }

  private showFullPrompt(): void {
    this.showCompactBanner = false;
    this.showInstallPrompt = true;
    this.promptShownTime = Date.now();
  }

  async installApp(): Promise<void> {
    const success = await this.pwaInstallService.showInstallPrompt();
    
    if (success) {
      this.showInstallPrompt = false;
      this.showCompactBanner = false;
      
      // Mostrar mensaje de éxito
      this.showSuccessMessage('¡Aplicación instalada correctamente! 🎉');
    } else {
      // Si falló, mostrar instrucciones manuales
      this.showInstallPrompt = false;
      this.showCompactBanner = false;
    }
  }

  dismissPrompt(): void {
    this.showInstallPrompt = false;
    this.pwaInstallService.dismissInstallReminder();
    
    // Si el usuario cerró el prompt rápidamente, mostrar el banner compacto
    const timeShown = Date.now() - this.promptShownTime;
    if (timeShown < 5000) { // Menos de 5 segundos
      setTimeout(() => {
        this.showCompactBanner = true;
      }, 60000); // Mostrar banner después de 1 minuto
    }
  }

  dismissBanner(): void {
    this.showCompactBanner = false;
    this.pwaInstallService.dismissInstallReminder();
  }

  async updateApp(): Promise<void> {
    const success = await this.pwaInstallService.activateUpdate();
    
    if (success) {
      this.showSuccessMessage('Actualizando aplicación... 🔄');
    } else {
      this.showErrorMessage('Error al actualizar. Recarga la página manualmente.');
    }
  }

  dismissUpdate(): void {
    this.showUpdateNotification = false;
  }

  private showSuccessMessage(message: string): void {
    // Crear notificación temporal de éxito
    const notification = document.createElement('div');
    notification.className = 'success-toast';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 10000;
      font-weight: 600;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  private showErrorMessage(message: string): void {
    // Similar a showSuccessMessage pero con estilo de error
    const notification = document.createElement('div');
    notification.className = 'error-toast';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ef4444;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 10000;
      font-weight: 600;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 5000);
  }
}